import type {
  RepositoryIntelligenceFileContent,
  RepositoryIntelligenceFileList,
  RepositoryIntelligenceSearchResult,
  RepositoryIntelligenceSymbol,
  RepositoryIntelligenceSymbolKind,
  RepositoryIntelligenceSymbolList,
} from '@ashniva/types';
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { apiRequest } from '../../shared/lib/api-client';
import { intelligencePath, riKeys, riRetry } from './api';

/**
 * Reading a repository's indexed code: search, files, a file's lines, symbols. Code is only ever held in the query
 * cache for the screen showing it — never written to storage, never logged, never put in a URL (a path and a line
 * number are the only things a URL carries).
 */

export function useRiSearchQuery(linkId: string, query: string, limit: number) {
  return useQuery({
    queryKey: [...riKeys.link(linkId), 'search', query, limit],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceSearchResult>(`${intelligencePath(linkId)}/search`, {
        method: 'POST',
        body: { query, limit },
      }),
    // One round trip per search the person asked for; never on focus or remount.
    enabled: query.length > 0,
    staleTime: Number.POSITIVE_INFINITY,
    retry: riRetry,
  });
}

export function useRiFilesQuery(linkId: string, prefix: string) {
  return useInfiniteQuery({
    queryKey: [...riKeys.link(linkId), 'files', prefix],
    queryFn: ({ pageParam }) =>
      apiRequest<RepositoryIntelligenceFileList>(`${intelligencePath(linkId)}/files`, {
        query: { prefix: prefix || undefined, cursor: pageParam, limit: 200 },
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    retry: riRetry,
  });
}

export function useRiFileContentQuery(
  linkId: string,
  path: string | null,
  startLine: number,
  endLine: number,
) {
  return useQuery({
    queryKey: [...riKeys.link(linkId), 'content', path, startLine, endLine],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceFileContent>(`${intelligencePath(linkId)}/files/content`, {
        query: { path: path ?? '', startLine, endLine },
      }),
    enabled: Boolean(path),
    placeholderData: keepPreviousData,
    retry: riRetry,
  });
}

export interface SymbolFilter {
  q: string;
  kind: RepositoryIntelligenceSymbolKind | '';
  path: string;
}

export function useRiSymbolsQuery(linkId: string, filter: SymbolFilter, enabled: boolean) {
  return useQuery({
    queryKey: [...riKeys.link(linkId), 'symbols', filter.q, filter.kind, filter.path],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceSymbolList>(`${intelligencePath(linkId)}/symbols`, {
        query: {
          q: filter.q || undefined,
          kind: filter.kind || undefined,
          path: filter.path || undefined,
          limit: 50,
        },
      }),
    enabled,
    retry: riRetry,
  });
}

export function useRiSymbolQuery(linkId: string, symbolId: string | null) {
  return useQuery({
    queryKey: [...riKeys.link(linkId), 'symbol', symbolId],
    queryFn: () =>
      apiRequest<RepositoryIntelligenceSymbol>(
        `${intelligencePath(linkId)}/symbols/${encodeURIComponent(symbolId ?? '')}`,
      ),
    enabled: Boolean(symbolId),
    retry: riRetry,
  });
}
